import {Flag, type FlagProps} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import i18n from '@dms/i18n';

import {auctionClock} from '../../model/auctionView';
import type {
  AuctionTransaction,
  AuctionTransactionStatus,
  DateTimeString,
} from '../../model/auctionView.types';
import {useAuctionFormat} from '../components/useAuctionFormat';
import {Countdown} from './MaxPriceCalculation';

const STATUS_COLOR: Record<AuctionTransactionStatus, FlagProps['colorScheme']> = {
  AWAITING_PAYMENT: 'orange',
  PAID: 'blue',
  DISPUTE_OPEN: 'red',
  DELIVERED: 'green',
  CANCELLED: 'neutral',
};

function Deadline(props: {
  caption: string;
  until: DateTimeString | null;
  doneLabel: string | null;
  now: Date;
  testId: string;
}) {
  const format = useAuctionFormat();
  return match({doneLabel: props.doneLabel, until: props.until})
    .with({doneLabel: Pattern.string}, ({doneLabel}) => (
      <VStack spacing={1} data-testid={props.testId}>
        <Text size="small" alternative color="success">
          {doneLabel}
        </Text>
        <Text size="xSmall" color="secondary">
          {props.caption}
        </Text>
      </VStack>
    ))
    .with({until: Pattern.nullish}, () => null)
    .with({until: Pattern.nonNullable}, ({until}) => {
      const clock = auctionClock(until, props.now);
      return (
        <Countdown
          until={until}
          now={props.now}
          caption={`${props.caption} · ${format.dateTime(until)}`}
          overdueLabel={i18n.t('entity.auction.clock.overdue', {
            days: clock.days,
            hours: clock.hours,
          })}
          testId={props.testId}
        />
      );
    })
    .exhaustive();
}

// The handover belongs to the sale business case. The auction shows only the status and the two deadlines.
export function TransactionStatus(props: {transaction: AuctionTransaction; now: Date}) {
  const format = useAuctionFormat();

  return (
    <VStack spacing={3} data-testid="auction-transaction">
      <HStack>
        <Flag
          label={i18n.t(`entity.auction.transaction.status.${props.transaction.status}`)}
          colorScheme={STATUS_COLOR[props.transaction.status]}
          isSubtle
          size="small"
          data-testid="auction-transaction-status"
        />
      </HStack>
      <Show when={props.transaction.status !== 'CANCELLED'}>
        <HStack spacing={6} wrap>
          <Deadline
            caption={i18n.t('entity.auction.clock.paymentDeadlineAt')}
            until={props.transaction.paymentDeadlineAt}
            doneLabel={
              props.transaction.paidAt === null
                ? null
                : i18n.t('entity.auction.handover.paid', {
                    date: format.dateTime(props.transaction.paidAt),
                  })
            }
            now={props.now}
            testId="auction-transaction-payment"
          />
          <Deadline
            caption={i18n.t('entity.auction.clock.pickupDeadlineAt')}
            until={props.transaction.pickupDeadlineAt}
            doneLabel={
              props.transaction.deliveredAt === null
                ? null
                : `${i18n.t('entity.auction.final.deliveredAt')} ${format.dateTime(props.transaction.deliveredAt)}`
            }
            now={props.now}
            testId="auction-transaction-pickup"
          />
        </HStack>
      </Show>
    </VStack>
  );
}
